import { z } from 'zod';
import { CASE_RISKS, CASE_STATUSES } from '../../types/domain';
import type {
  FindingInput,
  InspectionObservationInput,
  LaborRelationsCaseInput,
} from '../../types/domain';

/**
 * Esquemas de los formularios de datos propios (§19).
 *
 * Los campos opcionales se modelan como cadena vacía en el formulario y se
 * convierten a `null` al enviar: un `<input>` nunca produce `null`, y arrastrar esa
 * diferencia hasta la API terminaría guardando cadenas vacías donde debería haber
 * ausencia de dato.
 */
const requiredDate = z.string().min(1, 'Indique la fecha.');
const optionalText = z.string().max(2000, 'El texto es demasiado extenso.');

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** Un registro cerrado exige fecha de cierre: sin ella la trazabilidad se pierde. */
function closureRefinement(values: { status: string; closedOn: string }): boolean {
  return values.status !== 'CLOSED' || values.closedOn.length > 0;
}

const CLOSURE_MESSAGE: { path: Array<string | number>; message: string } = {
  path: ['closedOn'],
  message: 'Un caso cerrado requiere fecha de cierre.',
};

// ─── Relaciones laborales ───────────────────────────────────────────────────

export const laborRelationsFormSchema = z
  .object({
    // Empresa opcional: el registro puede quedar a nivel general. El área se
    // encadena a la empresa, así que también es opcional (sin empresa no hay
    // área que elegir).
    companyId: z.string(),
    areaId: z.string(),
    caseType: z.string().min(3, 'Indique el tipo de caso.').max(160),
    occurredOn: requiredDate,
    description: z
      .string()
      .min(10, 'Describa el caso con al menos 10 caracteres.')
      .max(4000, 'La descripción es demasiado extensa.'),
    risk: z.enum(CASE_RISKS),
    responsible: z.string().min(3, 'Indique el responsable.').max(160),
    action: z.string().min(5, 'Indique la acción comprometida.').max(2000),
    dueDate: z.string(),
    status: z.enum(CASE_STATUSES),
    comment: optionalText,
    evidenceReference: z.string().max(500, 'La referencia es demasiado extensa.'),
    closedOn: z.string(),
  })
  .refine(closureRefinement, CLOSURE_MESSAGE);

export type LaborRelationsFormValues = z.infer<typeof laborRelationsFormSchema>;

export const LABOR_RELATIONS_DEFAULTS: LaborRelationsFormValues = {
  companyId: '',
  areaId: '',
  caseType: '',
  occurredOn: '',
  description: '',
  risk: 'MEDIUM',
  responsible: '',
  action: '',
  dueDate: '',
  status: 'OPEN',
  comment: '',
  evidenceReference: '',
  closedOn: '',
};

export function toLaborRelationsInput(values: LaborRelationsFormValues): LaborRelationsCaseInput {
  return {
    companyId: emptyToNull(values.companyId),
    areaId: emptyToNull(values.areaId),
    caseType: values.caseType.trim(),
    occurredOn: values.occurredOn,
    description: values.description.trim(),
    risk: values.risk,
    responsible: values.responsible.trim(),
    action: values.action.trim(),
    dueDate: emptyToNull(values.dueDate),
    status: values.status,
    comment: emptyToNull(values.comment),
    evidenceReference: emptyToNull(values.evidenceReference),
    closedOn: emptyToNull(values.closedOn),
  };
}

// ─── Fiscalizaciones ────────────────────────────────────────────────────────

export const inspectionFormSchema = z
  .object({
    // Empresa opcional: el registro puede quedar a nivel general. El área se
    // encadena a la empresa, así que también es opcional (sin empresa no hay
    // área que elegir).
    companyId: z.string(),
    areaId: z.string(),
    authority: z.string().min(3, 'Indique el organismo fiscalizador.').max(160),
    inspectionType: z.string().min(3, 'Indique el tipo de fiscalización.').max(160),
    inspectedOn: requiredDate,
    subject: z.string().min(3, 'Indique la materia fiscalizada.').max(240),
    observation: z
      .string()
      .min(10, 'Registre la observación con al menos 10 caracteres.')
      .max(4000, 'La observación es demasiado extensa.'),
    risk: z.enum(CASE_RISKS),
    responsible: z.string().min(3, 'Indique el responsable.').max(160),
    action: z.string().min(5, 'Indique la acción comprometida.').max(2000),
    dueDate: z.string(),
    status: z.enum(CASE_STATUSES),
    fineAmount: z.string(),
    evidenceReference: z.string().max(500, 'La referencia es demasiado extensa.'),
    closedOn: z.string(),
  })
  .refine(closureRefinement, CLOSURE_MESSAGE)
  .refine(
    (values) => values.fineAmount.length === 0 || Number.isFinite(Number(values.fineAmount)),
    { path: ['fineAmount'], message: 'El monto debe ser un número.' },
  );

export type InspectionFormValues = z.infer<typeof inspectionFormSchema>;

export const INSPECTION_DEFAULTS: InspectionFormValues = {
  companyId: '',
  areaId: '',
  authority: '',
  inspectionType: '',
  inspectedOn: '',
  subject: '',
  observation: '',
  risk: 'MEDIUM',
  responsible: '',
  action: '',
  dueDate: '',
  status: 'OPEN',
  fineAmount: '',
  evidenceReference: '',
  closedOn: '',
};

export function toInspectionInput(values: InspectionFormValues): InspectionObservationInput {
  return {
    companyId: emptyToNull(values.companyId),
    areaId: emptyToNull(values.areaId),
    authority: values.authority.trim(),
    inspectionType: values.inspectionType.trim(),
    inspectedOn: values.inspectedOn,
    subject: values.subject.trim(),
    observation: values.observation.trim(),
    risk: values.risk,
    responsible: values.responsible.trim(),
    action: values.action.trim(),
    dueDate: emptyToNull(values.dueDate),
    status: values.status,
    fineAmount: values.fineAmount.trim().length === 0 ? null : Number(values.fineAmount),
    evidenceReference: emptyToNull(values.evidenceReference),
    closedOn: emptyToNull(values.closedOn),
  };
}

// ─── Hallazgos ──────────────────────────────────────────────────────────────

export const findingFormSchema = z
  .object({
    // Empresa opcional: el registro puede quedar a nivel general. El área se
    // encadena a la empresa, así que también es opcional (sin empresa no hay
    // área que elegir).
    companyId: z.string(),
    areaId: z.string(),
    indicatorKey: z.string().min(2, 'Indique el indicador afectado.').max(80),
    indicatorLabel: z.string().min(3, 'Indique el nombre del indicador.').max(160),
    risk: z.enum(CASE_RISKS),
    action: z.string().min(5, 'Indique la acción propuesta.').max(2000),
    responsible: z.string().min(3, 'Indique el responsable.').max(160),
    dueDate: z.string(),
    status: z.enum(CASE_STATUSES),
    comment: optionalText,
    detectedOn: requiredDate,
    closedOn: z.string(),
  })
  .refine(closureRefinement, CLOSURE_MESSAGE);

export type FindingFormValues = z.infer<typeof findingFormSchema>;

export const FINDING_DEFAULTS: FindingFormValues = {
  companyId: '',
  areaId: '',
  indicatorKey: '',
  indicatorLabel: '',
  risk: 'MEDIUM',
  action: '',
  responsible: '',
  dueDate: '',
  status: 'OPEN',
  comment: '',
  detectedOn: '',
  closedOn: '',
};

export function toFindingInput(values: FindingFormValues): FindingInput {
  return {
    companyId: emptyToNull(values.companyId),
    areaId: emptyToNull(values.areaId),
    indicatorKey: values.indicatorKey.trim(),
    indicatorLabel: values.indicatorLabel.trim(),
    risk: values.risk,
    action: values.action.trim(),
    responsible: values.responsible.trim(),
    dueDate: emptyToNull(values.dueDate),
    status: values.status,
    comment: emptyToNull(values.comment),
    detectedOn: values.detectedOn,
    closedOn: emptyToNull(values.closedOn),
  };
}
